import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core"
import { capitalize } from "lodash"

import { DEFAULT_PAGE_SIZE } from "@app/cadastro/admin/lib/constants"
import { OrderTableEnum } from "@app/cadastro/admin/models/enums/orderTableEnum"
import { FilterState } from "@app/cadastro/admin/services/states/filter.state"
import { LexTranslateService } from "@app/core/services/lex-translate.service"
import { Router } from "@angular/router"

export interface ILexTable {
  columns: Column[]
  data: any[]
  subtitle?: Subtitle
  status?: Status
}

interface Column {
  id: string
  name?: string
  togglePropertyName?: string
  togglePropertyChange?: () => void
  width?: string
  count?: number
  foldable?: {
    content: string
    bindLabel?: any
    contentWidth?: any
    ngStyle?: any
  }
  foldableStatus?: {
    content: string
    bindLabel?: any
    contentWidth?: any
    ngStyle?: any
  }
  pill?: {
    labeling: () => string
    styling: () => object
    propertyName: string
  }
  iconText?: { propertyName: string; iconWidth?: string }
  ordination?: {
    orderType: number
  }
  ngClass?: string
  ngStyle?: object
  actionButton?: any
  actionGroupButton?: any
  disabledDeleteButton?: boolean
}

interface Subtitle {
  name: string
  data: SubtitleData[]
}

interface SubtitleData {
  ngStyle: object
  ngClass: string
  label: string
}

export interface Status {
  colorName: string
  label: string
}

@Component({
  selector: "seb-lex-table",
  templateUrl: "./lex-table.component.html",
  styleUrls: ["./lex-table.component.css"],
})
export class LexTableComponent implements OnInit {
  @Input() data: any[]
  @Input() columns: Column[]
  @Input() subtitle: Subtitle
  @Input() status: Status
  @Input() showStatus = false
  @Input() showCheckboxes = false
  @Input() preloader: boolean
  @Input() noDataText: string
  @Input() inModal = false
  @Input() tableHistoric = false
  @Input() showPagination = false
  @Input() paginationSize: number
  @Input() initialPage: number
  @Input() readonly = false
  @Input() ngClass: string
  @Input() rowSelect: boolean
  @Input() disableLineClick = false
  @Input() isSubjectSetTable: boolean = false
  @Input() notScrollTop: boolean = false
  @Input() itemSelected: string = ""

  @Output() changePage = new EventEmitter()
  @Output() openItemEdit = new EventEmitter()
  @Output() actionButton = new EventEmitter()
  @Output() sortColumn = new EventEmitter()
  @Output() evModalSelectedItems = new EventEmitter()

  borderSelected: string = "2px solid #68bfac"
  selectedItemsIds = {}
  itemsPerPage = DEFAULT_PAGE_SIZE
  objSubtitle: Subtitle
  showSubtitle = false
  rowSelected: boolean
  capitalize = capitalize

  constructor(
    private filterState: FilterState,
    public lextranslate: LexTranslateService,
    private router: Router,
  ) {}

  async ngOnInit() {
    await this.lextranslate.loadTranslationsInFunctionOfTenant()
    this.noDataText =
      this.lextranslate.i18n["4079c171-e6e1-4a9d-b741-e8fa02c8dcb7"] + "!" ??
      this.noDataText

    this.handleInitialTableConfig()
    this.handleStateSort()
  }

  handleInitialTableConfig() {
    if (this.subtitle) {
      this.showSubtitle = true
    }

    if (!this.initialPage) {
      this.initialPage = 1
    }

    if (this.tableHistoric) {
      this.itemsPerPage = 1
    }

    if (this.isSubjectSetTable) {
      this.itemsPerPage = 5
    }
  }

  handleStateSort() {
    const stateOrdination = this.filterState.states.ordination
    if (stateOrdination.fieldNameSort) {
      const newColumns = this.columns.map((col) => {
        if (col.id === stateOrdination.fieldNameSort && col.ordination) {
          if (stateOrdination.isDesc) {
            return {
              ...col,
              ordination: { orderType: OrderTableEnum.Descending },
            }
          } else {
            return {
              ...col,
              ordination: { orderType: OrderTableEnum.Ascending },
            }
          }
        } else if (col.ordination) {
          return { ...col, ordination: { orderType: OrderTableEnum.NotOrder } }
        } else {
          return col
        }
      })
      this.columns = newColumns
    }
  }

  redirectToItemEdit(event, item) {
    if (
      this.readonly ||
      event.target.classList.contains("js-action") ||
      event.target.classList.contains("input-span") ||
      event.target.localName === "input"
    ) {
      return
    }

    if (!this.rowSelected) {
      this.itemSelected = item.serviceConfigurationId
      this.rowSelected = true
    } else {
      this.itemSelected = ""
      this.rowSelected = false
    }

    const selection = window.getSelection()

    requestAnimationFrame(() => {
      const hasSelection = selection
        ? selection.type
          ? selection.type.toLowerCase() === "range"
          : selection.toString().length > 0
        : false

      if (!hasSelection) {
        this.openItemEdit.emit(item)
      }
    })
  }

  setItemSelected(item: string) {
    this.itemSelected = item
  }

  callAction($event, actionId, rowData) { 
    this.actionButton.emit({ actionId, rowData, $event })
  }

  toggleSelection(oneData) {
    if (
      oneData._checkbox &&
      (oneData._checkbox.disabled || oneData._checkbox.hidden)
    ) {
      return
    }

    this.selectedItemsIds[oneData.id] = this.selectedItemsIds[oneData.id]
      ? null
      : oneData
  }

  get areAllSelected() {
    const itemsAmount = this.data.filter(
      (item) =>
        !(item._checkbox && (item._checkbox.disabled || item._checkbox.hidden))
    ).length
    return (
      itemsAmount > 0 &&
      this.selectedItems.filter(
        (item) => !!this.data.find((c) => c.id === item.id)
      ).length === itemsAmount
    )
  }

  get selectedItems(): Array<any> {
    return Object.values(this.selectedItemsIds).filter((item) => !!item)
  }

  get countSelectedItems() {
    this.lextranslate.setParams(
      "items.length",
      String(this.selectedItems.length)
    )

    return this.selectedItems.length
  }

  get selectedItemsPrimary() {
    const selectedItems = this.selectedItems
    const primaryValues = {}

    for (const selectedItem of selectedItems) {
      primaryValues[selectedItem.id] = true
    }

    return primaryValues
  }

  toggleAllSelection() {
    const wereAllSelected = this.areAllSelected
    const selectAvailableItems = this.data.filter(
      (item) =>
        !(item._checkbox && (item._checkbox.disabled || item._checkbox.hidden))
    )
    for (const item of selectAvailableItems) {
      this.selectedItemsIds[item.id] = wereAllSelected ? null : item
    }
  }

  onIndexPag(indexPage) {
    this.changePage.emit(indexPage)
  }

  onSortColumn(column) {
    let isDesc: boolean
    const columnOrderType = column.ordination.orderType
    if (
      columnOrderType === OrderTableEnum.Descending ||
      columnOrderType === OrderTableEnum.NotOrder
    ) {
      column.ordination.orderType = OrderTableEnum.Ascending
      isDesc = false
    } else if (columnOrderType === OrderTableEnum.Ascending) {
      column.ordination.orderType = OrderTableEnum.Descending
      isDesc = true
    }

    const newColumns = this.columns.map((col) => {
      if (col.id !== column.id && col.ordination) {
        return { ...col, ordination: { orderType: OrderTableEnum.NotOrder } }
      } else {
        return col
      }
    })
    this.columns = newColumns

    this.sortColumn.emit({ id: column.id, isDesc })
  }

  openModalSelectedItems() {
    this.evModalSelectedItems.emit()
  }

  clearAllSelectedItems() {
    this.selectedItemsIds = {}
  }
}
